import { useMemo, useCallback } from 'react'
import { collection, query, where, serverTimestamp } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { addDoc, deleteDoc } from '../lib/firestore'
import { doc } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { runMutation } from '../lib/firestoreMutation'
import { track, type AnalyticsEvents } from '../lib/analytics'
import type { Group, GroupInvite } from '../types'
import { fbt } from 'fbtee'

export function useGroupInvites(groupId: string | null) {
  const q = useMemo(
    () =>
      groupId
        ? (query(
            collection(db, 'groupInvites'),
            where('groupId', '==', groupId),
          ) as Query<GroupInvite>)
        : null,
    [groupId],
  )

  const { data: invites, loading } = useFirestoreCollection(q)

  const sendInvite = useCallback(
    async (
      toUserId: string,
      fromUserId: string,
      group: Group,
      method: AnalyticsEvents['group_invite_sent']['method'] = 'friend_list',
    ) => {
      await runMutation(fbt('Failed to send invite', 'Error toast when sending a group invite fails'), async () => {
        await addDoc(collection(db, 'groupInvites'), {
          groupId: group.id,
          toUserId,
          fromUserId,
          groupName: group.name,
          ...(group.imageUrl ? { groupImageUrl: group.imageUrl } : {}),
          createdAt: serverTimestamp(),
        })
        track('group_invite_sent', { group_id: group.id, method })
      })
    },
    [],
  )

  const revokeInvite = useCallback(async (inviteId: string) => {
    await runMutation(fbt('Failed to remove invite', 'Error toast when removing a pending group invite fails'), async () => {
      await deleteDoc(doc(db, 'groupInvites', inviteId))
    })
  }, [])

  return { invites, loading, sendInvite, revokeInvite }
}
